import React, { useState, useMemo, useEffect } from 'react';
import { Search, MapPin, Filter, Star, Crown, Clock, X, Map, List, SlidersHorizontal, ChevronDown, ChevronUp, ArrowLeft, Calendar, Users, Package, Building2, Scissors, Sparkles, Dumbbell, Car, Check, Ticket, BookOpen, Navigation, Loader2 } from 'lucide-react';
import { CATEGORIES, OFFICES, SERVICES, PRODUCTS, COURSES } from '../data/database';
import OfficeCard from '../components/OfficeCard';
import { ServiceCard, ProductCard, CourseCard } from '../components/ItemCards';

const SEARCH_TYPES = [
  { id: 'all', label: 'Tutto' },
  { id: 'offices', label: 'Attività' },
  { id: 'services', label: 'Servizi' },
  { id: 'products', label: 'Prodotti' },
  { id: 'events', label: 'Eventi' },
  { id: 'courses', label: 'Corsi' }
];

const SORT_OPTIONS = [
  { id: 'relevance', label: 'Rilevanza' },
  { id: 'rating', label: 'Valutazione' },
  { id: 'distance', label: 'Distanza' },
  { id: 'price_asc', label: 'Prezzo ↑' },
  { id: 'price_desc', label: 'Prezzo ↓' }
];

// Smart filters per categoria
const SMART_FILTERS = {
  hotel: {
    type: 'booking',
    label: 'Prenotazione Hotel',
    fields: [
      { id: 'checkin', label: 'Check-in', type: 'date', icon: Calendar },
      { id: 'checkout', label: 'Check-out', type: 'date', icon: Calendar },
      { id: 'guests', label: 'Ospiti', type: 'number', icon: Users, min: 1, max: 10 },
      { id: 'rooms', label: 'Camere', type: 'number', icon: Building2, min: 1, max: 5 },
    ]
  },
  taxi: {
    type: 'transport',
    label: 'Prenotazione Taxi',
    fields: [
      { id: 'pickup', label: 'Partenza', type: 'location', icon: MapPin },
      { id: 'dropoff', label: 'Arrivo', type: 'location', icon: MapPin },
      { id: 'datetime', label: 'Quando', type: 'datetime', icon: Calendar },
      { id: 'passengers', label: 'Passeggeri', type: 'number', icon: Users, min: 1, max: 8 },
    ]
  },
  ncc: {
    type: 'transport',
    label: 'Noleggio con Conducente',
    fields: [
      { id: 'pickup', label: 'Partenza', type: 'location', icon: MapPin },
      { id: 'dropoff', label: 'Arrivo', type: 'location', icon: MapPin },
      { id: 'datetime', label: 'Quando', type: 'datetime', icon: Calendar },
      { id: 'hours', label: 'Ore', type: 'number', icon: Clock, min: 1, max: 12 },
    ]
  },
  ristoranti: {
    type: 'booking',
    label: 'Prenotazione Tavolo',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'guests', label: 'Persone', type: 'number', icon: Users, min: 1, max: 20 },
      { id: 'delivery', label: 'Modalità', type: 'select', icon: Package, options: ['Sul posto', 'Asporto', 'Delivery'] },
    ]
  },
  pizzerie: {
    type: 'booking',
    label: 'Ordine Pizzeria',
    fields: [
      { id: 'delivery', label: 'Modalità', type: 'select', icon: Package, options: ['Sul posto', 'Asporto', 'Delivery'] },
      { id: 'time', label: 'Ora ritiro/consegna', type: 'time', icon: Clock },
    ]
  },
  parrucchieri: {
    type: 'appointment',
    label: 'Appuntamento',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'service', label: 'Servizio', type: 'select', icon: Scissors, options: ['Taglio', 'Colore', 'Piega', 'Trattamento'] },
    ]
  },
  barbieri: {
    type: 'appointment',
    label: 'Appuntamento',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'service', label: 'Servizio', type: 'select', icon: Scissors, options: ['Taglio', 'Barba', 'Taglio + Barba'] },
    ]
  },
  estetisti: {
    type: 'appointment',
    label: 'Appuntamento',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'service', label: 'Trattamento', type: 'select', icon: Sparkles, options: ['Manicure', 'Pedicure', 'Pulizia viso', 'Massaggio', 'Ceretta'] },
    ]
  },
  palestre: {
    type: 'subscription',
    label: 'Iscrizione',
    fields: [
      { id: 'type', label: 'Tipo', type: 'select', icon: Dumbbell, options: ['Abbonamento', 'Ingresso singolo', 'Corso'] },
      { id: 'date', label: 'Data inizio', type: 'date', icon: Calendar },
    ]
  },
  medici: {
    type: 'appointment',
    label: 'Visita Medica',
    fields: [
      { id: 'specialty', label: 'Specialità', type: 'select', icon: Building2, options: ['Generico', 'Dermatologo', 'Cardiologo', 'Ortopedico'] },
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'visitType', label: 'Tipo', type: 'select', icon: Users, options: ['In studio', 'Videoconsulto', 'Domiciliare'] },
    ]
  },
  dentisti: {
    type: 'appointment',
    label: 'Visita Dentistica',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'visitType', label: 'Tipo', type: 'select', icon: Users, options: ['Controllo', 'Pulizia', 'Cura', 'Urgenza'] },
    ]
  },
  fisioterapisti: {
    type: 'appointment',
    label: 'Seduta Fisioterapia',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'time', label: 'Ora', type: 'time', icon: Clock },
      { id: 'type', label: 'Tipo', type: 'select', icon: Users, options: ['Prima visita', 'Seduta', 'Pacchetto'] },
    ]
  },
  meccanici: {
    type: 'appointment',
    label: 'Appuntamento Officina',
    fields: [
      { id: 'date', label: 'Data', type: 'date', icon: Calendar },
      { id: 'service', label: 'Servizio', type: 'select', icon: Car, options: ['Tagliando', 'Riparazione', 'Diagnosi', 'Gomme'] },
    ]
  }
};

// Detect category from query
const detectCategoryFromQuery = (query) => {
  if (!query) return null;
  const q = query.toLowerCase();
  if (q.includes('taxi') || q.includes('transfer')) return 'taxi';
  if (q.includes('ncc') || q.includes('noleggio con conducente')) return 'ncc';
  if (q.includes('hotel') || q.includes('albergo')) return 'hotel';
  if (q.includes('ristorante') || q.includes('trattoria')) return 'ristoranti';
  if (q.includes('pizza')) return 'pizzerie';
  if (q.includes('parrucchier') || q.includes('capelli')) return 'parrucchieri';
  if (q.includes('barbier') || q.includes('barba')) return 'barbieri';
  if (q.includes('estetist') || q.includes('manicure')) return 'estetisti';
  if (q.includes('palestr') || q.includes('fitness')) return 'palestre';
  if (q.includes('medic') || q.includes('dottor')) return 'medici';
  if (q.includes('dentist')) return 'dentisti';
  if (q.includes('fisio')) return 'fisioterapisti';
  if (q.includes('meccanico') || q.includes('officina')) return 'meccanici';
  return null;
};

const SearchPage = ({ onNavigate, onBack, initialQuery = '', initialCategory = '', initialFilter = '', initialView = 'list', initialLocation = 'Napoli', initialFilters = {}, detectFilters = false }) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchType, setSearchType] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [viewMode, setViewMode] = useState(initialView);
  const [location, setLocation] = useState(initialLocation);
  const [sortBy, setSortBy] = useState('relevance');
  
  // Modal states
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  
  // Smart filters
  const [detectedCategory, setDetectedCategory] = useState(null);
  const [showSmartFilters, setShowSmartFilters] = useState(false);
  const [smartFilterValues, setSmartFilterValues] = useState(initialFilters || {});

  // Quick filters
  const [showElitOnly, setShowElitOnly] = useState(false);
  const [showOffersOnly, setShowOffersOnly] = useState(false);
  const [minRating, setMinRating] = useState(0);
  
  // Price range filter
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  // Suggested locations
  const SUGGESTED_LOCATIONS = [
    { name: 'Napoli', region: 'Campania', distance: null },
    { name: 'Roma', region: 'Lazio', distance: '220 km' },
    { name: 'Milano', region: 'Lombardia', distance: '770 km' },
    { name: 'Firenze', region: 'Toscana', distance: '470 km' },
    { name: 'Salerno', region: 'Campania', distance: '55 km' },
    { name: 'Caserta', region: 'Campania', distance: '30 km' },
  ];

  const filteredLocations = SUGGESTED_LOCATIONS.filter(loc => 
    loc.name.toLowerCase().includes(locationSearch.toLowerCase()) ||
    loc.region.toLowerCase().includes(locationSearch.toLowerCase())
  );

  const handleUseGPS = () => {
    setIsLocating(true);
    // Simula geolocalizzazione
    setTimeout(() => {
      setLocation('Posizione attuale');
      setIsLocating(false);
      setShowLocationModal(false);
    }, 1500);
  };

  const selectLocation = (loc) => {
    setLocation(loc.name);
    setShowLocationModal(false);
    setLocationSearch('');
  };

  // Detect category
  useEffect(() => {
    const detected = initialCategory || detectCategoryFromQuery(searchQuery);
    if (detected && SMART_FILTERS[detected]) {
      setDetectedCategory(detected);
      setShowSmartFilters(true);
    } else {
      setDetectedCategory(null);
      setShowSmartFilters(false);
    }
  }, [searchQuery, initialCategory]);

  const updateFilterValue = (fieldId, value) => {
    setSmartFilterValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const getSmartFilters = () => SMART_FILTERS[detectedCategory] || null;

  const clearSmartFilters = () => {
    setSmartFilterValues({});
    setDetectedCategory(null);
    setShowSmartFilters(false);
    setActiveCategory('');
  };

  const renderFilterField = (field) => {
    const IconComponent = field.icon;
    const value = smartFilterValues[field.id] || '';

    const baseInputClass = "w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none bg-white";

    switch (field.type) {
      case 'date':
        return (
          <div key={field.id} className="flex-1 min-w-[120px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="date" value={value} onChange={(e) => updateFilterValue(field.id, e.target.value)} className={baseInputClass} />
            </div>
          </div>
        );
      case 'time':
        return (
          <div key={field.id} className="flex-1 min-w-[100px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="time" value={value} onChange={(e) => updateFilterValue(field.id, e.target.value)} className={baseInputClass} />
            </div>
          </div>
        );
      case 'datetime':
        return (
          <div key={field.id} className="flex-1 min-w-[160px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="datetime-local" value={value} onChange={(e) => updateFilterValue(field.id, e.target.value)} className={baseInputClass} />
            </div>
          </div>
        );
      case 'number':
        return (
          <div key={field.id} className="flex-1 min-w-[80px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="number" min={field.min || 1} max={field.max || 99} value={value} onChange={(e) => updateFilterValue(field.id, e.target.value)} placeholder={`${field.min || 1}`} className={baseInputClass} />
            </div>
          </div>
        );
      case 'select':
        return (
          <div key={field.id} className="flex-1 min-w-[120px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <select value={value} onChange={(e) => updateFilterValue(field.id, e.target.value)} className={`${baseInputClass} appearance-none pr-8`}>
                <option value="">Seleziona</option>
                {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        );
      case 'location':
        return (
          <div key={field.id} className="flex-1 min-w-[140px]">
            <label className="text-xs text-gray-500 mb-1 block">{field.label}</label>
            <div className="relative">
              <IconComponent className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={value} onChange={(e) => updateFilterValue(field.id, e.target.value)} placeholder="Indirizzo..." className={baseInputClass} />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Filter offices
  const filteredOffices = useMemo(() => {
    return OFFICES.filter(o => {
      if (showElitOnly && !o.isElit) return false;
      if (minRating > 0 && o.rating < minRating) return false;
      if (activeCategory && o.categorySlug !== activeCategory) return false;
      if (detectedCategory && o.categorySlug !== detectedCategory) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!o.name.toLowerCase().includes(query) && !o.category.toLowerCase().includes(query)) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'distance') return parseFloat(a.distance) - parseFloat(b.distance);
      return 0;
    });
  }, [activeFilter, activeCategory, searchQuery, detectedCategory, showElitOnly, minRating, sortBy]);

  // Filter services
  const filteredServices = useMemo(() => {
    return SERVICES.filter(s => {
      if (showElitOnly && !s.isElit) return false;
      if (showOffersOnly && !s.originalPrice) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!s.name.toLowerCase().includes(query) && !s.category?.toLowerCase().includes(query)) return false;
      }
      if (activeCategory || detectedCategory) {
        const office = OFFICES.find(o => o.id === s.officeId);
        if (office?.categorySlug !== (activeCategory || detectedCategory)) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      return 0;
    });
  }, [activeFilter, activeCategory, searchQuery, detectedCategory, showElitOnly, showOffersOnly, sortBy]);

  // Filter products
  const filteredProducts = useMemo(() => {
    return PRODUCTS.filter(p => {
      if (showElitOnly && !p.isElit) return false;
      if (showOffersOnly && !p.originalPrice) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!p.name.toLowerCase().includes(query) && !p.category?.toLowerCase().includes(query)) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      return 0;
    });
  }, [activeFilter, searchQuery, showElitOnly, showOffersOnly, sortBy]);

  // Filter events - modulo eventi non ancora implementato
  const filteredEvents = [];

  // Filter courses
  const filteredCourses = useMemo(() => {
    if (!COURSES) return [];
    return COURSES.filter(c => {
      if (showElitOnly && !c.isElit) return false;
      if (showOffersOnly && !c.originalPrice) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!c.name.toLowerCase().includes(query) && 
            !c.category?.toLowerCase().includes(query) &&
            !c.description?.toLowerCase().includes(query)) return false;
      }
      return true;
    }).sort((a, b) => {
      if (sortBy === 'price_asc') return a.price - b.price;
      if (sortBy === 'price_desc') return b.price - a.price;
      return 0;
    });
  }, [searchQuery, showElitOnly, showOffersOnly, sortBy]);

  const totalResults = 
    (searchType === 'all' || searchType === 'offices' ? filteredOffices.length : 0) +
    (searchType === 'all' || searchType === 'services' ? filteredServices.length : 0) +
    (searchType === 'all' || searchType === 'products' ? filteredProducts.length : 0) +
    (searchType === 'all' || searchType === 'events' ? filteredEvents.length : 0) +
    (searchType === 'all' || searchType === 'courses' ? filteredCourses.length : 0);

  const categoryInfo = detectedCategory ? CATEGORIES.find(c => c.slug === detectedCategory) : null;
  
  // Count active filters
  const activeFiltersCount = [showElitOnly, showOffersOnly, minRating > 0, priceRange.min || priceRange.max].filter(Boolean).length;

  // Reset all filters
  const resetFilters = () => {
    setShowElitOnly(false);
    setShowOffersOnly(false);
    setMinRating(0);
    setPriceRange({ min: '', max: '' });
    setSmartFilterValues({});
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-30 shadow-sm">
        <div className="max-w-screen-2xl mx-auto">
          {/* Search Bar */}
          <div className="p-4 pb-2">
            <div className="flex items-center gap-3">
              {onBack && (
                <button onClick={onBack} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-testid="search-back-btn">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
              )}
              <div className="flex-1 flex items-center gap-3 bg-gray-100 rounded-xl p-3">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca..."
                  className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-400"
                  autoFocus
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')}>
                    <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Smart Filters Indicator */}
          {showSmartFilters && getSmartFilters() && (
            <div className="px-4 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {categoryInfo && <span className="text-lg">{categoryInfo.icon}</span>}
                  <span className="text-sm font-medium text-gray-900">Ricerca per {categoryInfo?.name || detectedCategory}</span>
                </div>
                <button 
                  onClick={clearSmartFilters}
                  className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 hover:bg-white/50 rounded"
                >
                  Rimuovi
                </button>
              </div>
            </div>
          )}

          {/* Filter & Sort Bar */}
          <div className="px-4 py-2 flex items-center gap-2 border-b border-gray-100 overflow-x-auto scrollbar-hide">
            {/* Location Button */}
            <button
              onClick={() => setShowLocationModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all flex-shrink-0"
              data-testid="location-btn"
            >
              <MapPin className="w-4 h-4 text-emerald-500" />
              {location}
              <ChevronDown className="w-3 h-3" />
            </button>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilterModal(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all flex-shrink-0 ${
                activeFiltersCount > 0 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="filter-btn"
            >
              <Filter className="w-4 h-4" />
              Filtri
              {activeFiltersCount > 0 && (
                <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px]">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            
            {/* Sort Button */}
            <button
              onClick={() => setShowSortModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all flex-shrink-0"
              data-testid="sort-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {SORT_OPTIONS.find(o => o.id === sortBy)?.label || 'Ordina'}
            </button>
            
            {/* Quick filter chips for active filters */}
            {showElitOnly && (
              <span className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0">
                <Crown className="w-3 h-3" />
                Elit
                <button onClick={() => setShowElitOnly(false)} className="ml-1 hover:text-amber-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {showOffersOnly && (
              <span className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0">
                Offerte
                <button onClick={() => setShowOffersOnly(false)} className="ml-1 hover:text-red-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {minRating > 0 && (
              <span className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0">
                <Star className="w-3 h-3" />
                {minRating}+
                <button onClick={() => setMinRating(0)} className="ml-1 hover:text-amber-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>

          {/* Search Type Tabs */}
          <div className="px-4 py-2 flex items-center gap-1 bg-gray-50 overflow-x-auto scrollbar-hide">
            {SEARCH_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setSearchType(type.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  searchType === type.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-screen-2xl mx-auto px-6 py-4 lg:py-6">
        {/* Results Count */}
        <div className="flex items-center justify-between mb-4 lg:mb-6">
          <p className="text-sm lg:text-base text-gray-600">
            <span className="font-semibold text-gray-900">{totalResults}</span> risultati
            {searchQuery && <span className="text-gray-400"> per &quot;{searchQuery}&quot;</span>}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-emerald-100 text-emerald-600' : 'text-gray-400'}`}
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`p-2 rounded-lg ${viewMode === 'map' ? 'bg-emerald-100 text-emerald-600' : 'text-gray-400'}`}
            >
              <Map className="w-5 h-5" />
            </button>
          </div>
        </div>

        {viewMode === 'map' ? (
          <div className="bg-gray-200 rounded-2xl h-96 flex items-center justify-center">
            <p className="text-gray-500">Mappa in arrivo...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Businesses */}
            {(searchType === 'all' || searchType === 'offices') && filteredOffices.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2 lg:text-lg">
                  <Building2 className="w-5 h-5 lg:w-6 lg:h-6 text-blue-500" />
                  Attività ({filteredOffices.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                  {filteredOffices.map((office) => (
                    <OfficeCard
                      key={office.id}
                      office={office}
                      layout="card"
                      onClick={() => onNavigate('office', { id: office.id })}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Services */}
            {(searchType === 'all' || searchType === 'services') && filteredServices.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2 lg:text-lg">
                  <Scissors className="w-5 h-5 lg:w-6 lg:h-6 text-purple-500" />
                  Servizi ({filteredServices.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
                  {filteredServices.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      showBusiness={true}
                      onClick={() => onNavigate('service-detail', { id: service.id })}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Products */}
            {(searchType === 'all' || searchType === 'products') && filteredProducts.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2 lg:text-lg">
                  <Package className="w-5 h-5 lg:w-6 lg:h-6 text-orange-500" />
                  Prodotti ({filteredProducts.length})
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 lg:gap-4">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      showBusiness={true}
                      onClick={() => onNavigate('product-detail', { id: product.id })}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Events */}
            {(searchType === 'all' || searchType === 'events') && filteredEvents.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2 lg:text-lg">
                  <Ticket className="w-5 h-5 lg:w-6 lg:h-6 text-purple-500" />
                  Eventi ({filteredEvents.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-5">
                  {filteredEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      showBusiness={true}
                      onClick={() => onNavigate('event-detail', { id: event.id })}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Courses */}
            {(searchType === 'all' || searchType === 'courses') && filteredCourses.length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2 lg:text-lg">
                  <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-blue-500" />
                  Corsi ({filteredCourses.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-5">
                  {filteredCourses.map((course) => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      showBusiness={true}
                      onClick={() => onNavigate('course-detail', { id: course.id })}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {totalResults === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Nessun risultato</h3>
                <p className="text-gray-500">Prova a modificare i filtri o la ricerca</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowFilterModal(false)}>
          <div 
            className="bg-white w-full sm:w-96 sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Filtri</h3>
              <button 
                onClick={() => setShowFilterModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-6">
              {/* Quick Filters */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Filtri rapidi</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowElitOnly(!showElitOnly)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      showElitOnly ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Crown className="w-4 h-4" />
                    Solo Elit
                  </button>
                  <button
                    onClick={() => setShowOffersOnly(!showOffersOnly)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      showOffersOnly ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Ticket className="w-4 h-4" />
                    Solo Offerte
                  </button>
                </div>
              </div>

              {/* Rating Filter */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Valutazione minima</h4>
                <div className="flex gap-2">
                  {[0, 3, 4, 4.5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setMinRating(rating)}
                      className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        minRating === rating 
                          ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Star className="w-4 h-4" />
                      {rating === 0 ? 'Tutte' : `${rating}+`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Fascia di prezzo</h4>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">Minimo</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                      <input
                        type="number"
                        placeholder="0"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                        className="w-full pl-8 pr-3 py-2.5 rounded-xl bg-gray-100 border-0 outline-none focus:ring-2 focus:ring-emerald-300"
                      />
                    </div>
                  </div>
                  <span className="text-gray-400 mt-5">-</span>
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">Massimo</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                      <input
                        type="number"
                        placeholder="∞"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                        className="w-full pl-8 pr-3 py-2.5 rounded-xl bg-gray-100 border-0 outline-none focus:ring-2 focus:ring-emerald-300"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Smart Filters (if detected) */}
              {showSmartFilters && getSmartFilters() && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">
                      Filtri per {categoryInfo?.name || detectedCategory}
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {getSmartFilters().fields.map(field => (
                      <div key={field.id}>
                        <label className="text-sm text-gray-600 mb-1.5 block">{field.label}</label>
                        {field.type === 'date' && (
                          <input
                            type="date"
                            value={smartFilterValues[field.id] || ''}
                            onChange={(e) => updateFilterValue(field.id, e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-gray-100 border-0 outline-none focus:ring-2 focus:ring-emerald-300"
                          />
                        )}
                        {field.type === 'time' && (
                          <input
                            type="time"
                            value={smartFilterValues[field.id] || ''}
                            onChange={(e) => updateFilterValue(field.id, e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-gray-100 border-0 outline-none focus:ring-2 focus:ring-emerald-300"
                          />
                        )}
                        {field.type === 'number' && (
                          <input
                            type="number"
                            placeholder={field.placeholder}
                            value={smartFilterValues[field.id] || ''}
                            onChange={(e) => updateFilterValue(field.id, e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-gray-100 border-0 outline-none focus:ring-2 focus:ring-emerald-300"
                          />
                        )}
                        {field.type === 'select' && (
                          <select
                            value={smartFilterValues[field.id] || ''}
                            onChange={(e) => updateFilterValue(field.id, e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl bg-gray-100 border-0 outline-none focus:ring-2 focus:ring-emerald-300"
                          >
                            <option value="">Seleziona...</option>
                            {field.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <button
                onClick={resetFilters}
                className="flex-1 py-3 rounded-xl text-gray-700 font-medium bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Resetta
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 py-3 rounded-xl text-white font-medium bg-emerald-500 hover:bg-emerald-600 transition-colors"
              >
                Applica
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sort Modal */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowSortModal(false)}>
          <div 
            className="bg-white w-full sm:w-80 sm:rounded-2xl rounded-t-2xl overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Ordina per</h3>
              <button 
                onClick={() => setShowSortModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Sort Options */}
            <div className="p-2">
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => { setSortBy(opt.id); setShowSortModal(false); }}
                  className={`w-full px-4 py-3 text-left rounded-xl flex items-center justify-between transition-all ${
                    sortBy === opt.id 
                      ? 'bg-emerald-50 text-emerald-700' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium">{opt.label}</span>
                  {sortBy === opt.id && <Check className="w-5 h-5 text-emerald-600" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowLocationModal(false)}>
          <div 
            className="bg-white w-full sm:w-96 sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Seleziona posizione</h3>
              <button 
                onClick={() => setShowLocationModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Search Input */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  placeholder="Cerca città o indirizzo..."
                  className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-400"
                />
                {locationSearch && (
                  <button onClick={() => setLocationSearch('')}>
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            {/* GPS Button */}
            <div className="p-4 border-b border-gray-100">
              <button
                onClick={handleUseGPS}
                disabled={isLocating}
                className="w-full flex items-center gap-3 p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors"
              >
                {isLocating ? (
                  <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                ) : (
                  <Navigation className="w-5 h-5 text-emerald-600" />
                )}
                <div className="text-left">
                  <p className="font-medium text-emerald-700">
                    {isLocating ? 'Localizzazione in corso...' : 'Usa la mia posizione'}
                  </p>
                  <p className="text-xs text-emerald-600">Attiva GPS per risultati più precisi</p>
                </div>
              </button>
            </div>

            {/* Locations List */}
            <div className="overflow-y-auto max-h-[40vh]">
              <p className="px-4 py-2 text-xs text-gray-500 font-medium">CITTÀ SUGGERITE</p>
              {filteredLocations.map((loc, idx) => (
                <button
                  key={idx}
                  onClick={() => selectLocation(loc)}
                  className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${
                    location === loc.name ? 'bg-emerald-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <MapPin className={`w-5 h-5 ${location === loc.name ? 'text-emerald-600' : 'text-gray-400'}`} />
                    <div className="text-left">
                      <p className={`font-medium ${location === loc.name ? 'text-emerald-700' : 'text-gray-900'}`}>
                        {loc.name}
                      </p>
                      <p className="text-xs text-gray-500">{loc.region}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {loc.distance && (
                      <span className="text-xs text-gray-400">{loc.distance}</span>
                    )}
                    {location === loc.name && (
                      <Check className="w-5 h-5 text-emerald-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPage;
